<template>
    <div v-if="show" ref="modalRoot" class="custom-modal-root" @keydown="handleKeydown">
        <div :class="['custom-modal-backdrop', { dark: theme == THEMES.DARK }]" @click="$emit('close')"></div>
        <div
            ref="dialog"
            :class="['custom-dialog', 'shadow', { dark: theme == THEMES.DARK }]"
            role="dialog"
            aria-modal="true"
            :aria-label="title"
            tabindex="-1"
        >
            <div class="custom-dialog-header">
                <div class="custom-title">{{ title }}</div>
                <button type="button" class="custom-dialog-close" @click="$emit('close')" aria-label="Close modal">
                    <i class="fas fa-close"></i>
                </button>
            </div>
            <slot />
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex"

export default {
    props: {
        show: { type: Boolean, default: false },
        title: { type: String, default: "" },
    },
    data() {
        return {
            previouslyFocusedElement: null,
        }
    },
    computed: {
        ...mapState("misc", ["theme", "THEMES"]),
    },
    watch: {
        show: {
            immediate: true,
            handler(nextShow) {
                if (nextShow) {
                    this.previouslyFocusedElement = document.activeElement
                    this.$nextTick(this.focusInitialElement)
                } else {
                    this.restoreFocus()
                }
            },
        },
    },
    beforeDestroy() {
        this.restoreFocus()
    },
    methods: {
        getFocusableElements() {
            if (!this.$refs.dialog) return []

            return Array.from(
                this.$refs.dialog.querySelectorAll(
                    [
                        "a[href]",
                        "button:not([disabled])",
                        "textarea:not([disabled])",
                        "input:not([disabled])",
                        "select:not([disabled])",
                        "[tabindex]:not([tabindex='-1'])",
                    ].join(",")
                )
            ).filter((element) => {
                const isHidden = element.hidden || element.getAttribute("aria-hidden") === "true"
                return !isHidden
            })
        },
        focusInitialElement() {
            const focusableElements = this.getFocusableElements()
            const target = focusableElements[0] || this.$refs.dialog
            if (target && typeof target.focus === "function") {
                target.focus()
            }
        },
        restoreFocus() {
            const target = this.previouslyFocusedElement
            this.previouslyFocusedElement = null
            if (target && document.contains(target) && typeof target.focus === "function") {
                target.focus()
            }
        },
        handleKeydown(event) {
            if (event.key === "Escape") {
                event.preventDefault()
                this.$emit("close")
                return
            }

            if (event.key !== "Tab") return

            const focusableElements = this.getFocusableElements()
            if (!focusableElements.length) {
                event.preventDefault()
                this.$refs.dialog.focus()
                return
            }

            const firstElement = focusableElements[0]
            const lastElement = focusableElements[focusableElements.length - 1]

            if (event.shiftKey && document.activeElement === firstElement) {
                event.preventDefault()
                lastElement.focus()
            } else if (!event.shiftKey && document.activeElement === lastElement) {
                event.preventDefault()
                firstElement.focus()
            }
        },
    },
}
</script>

<style lang="scss">
@import "@/assets/variables.scss";
@import "@/assets/mixins.scss";
.custom-title {
    font-size: 18px;
}

.custom-dialog {
    position: fixed;
    max-width: 500px;
    width: 80%;
    min-height: 250px;
    border-radius: 10px;
    top: 20%;
    left: 50%;
    transform: translate(-50%);
    background-color: $lightCardBg;
    padding: 15px;
    z-index: 1050;
    
    &.dark {
        background-color: $darkCardBg;
        border: 1px solid $darkBorderColor;
        
        .custom-title {
            color: $darkTextColor;
        }
        
        .custom-dialog-close {
            color: $darkTextColor;
            &:focus-visible { @include focus-ring; }
        }
    }
}
.custom-dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}
.custom-dialog-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: transparent;
    cursor: pointer;
    color: inherit;
}
.custom-modal-backdrop {
    position: fixed;
    left: 0;
    top: 0;
    z-index: 1040;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
}
</style>
